import { ChatTheme } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { Icon, cn } from "@sagot/ui";
import { useState } from "react";
import { widgetFontStack } from "@sagot/constants";
import { useWidgetFontStylesheet } from "./font-picker";
import { LauncherMark } from "./launcher-mark";
import { WidgetPanelPreview } from "./widget-panel-preview";
import { brandContrast, type WidgetForm } from "./widget-model";

const P = "deployKnowledge.widgetStudio.preview";
const FILL = "bg-[var(--wg-primary)] text-[var(--wg-on-primary)]";

export type PreviewDevice = "desktop" | "mobile";

interface WidgetPreviewProps {
  form: WidgetForm;
  agentName: string;
  defaultGreeting: string;
  siteName: string;
  domain: string | null;
  device: PreviewDevice;
}

export function resolvedTheme(theme: WidgetForm["appearance"]["theme"]): "light" | "dark" {
  if (theme !== "auto") return theme;
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function MockSite({ siteName, domain }: { siteName: string; domain: string | null }) {
  const { t } = useTranslation();
  return (
    <div aria-hidden="true" className="bg-ink-50 text-ink-900">
      <div className="flex items-center justify-between border-b border-ink-100 px-8 py-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex size-9 items-center justify-center rounded-sm bg-ink-900 text-[11px] font-extrabold text-ink-50">
            <Icon name="building" className="size-4" />
          </span>
          <div className="leading-tight">
            <div className="text-sm font-extrabold">{siteName}</div>
            <div className="text-[10px] text-ink-600">{domain ?? t(`${P}.siteName`)}</div>
          </div>
        </div>
        <div className="hidden gap-5 md:flex">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="h-2.5 w-14 rounded-xs bg-ink-100" />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-8 px-8 py-10">
        <div className="col-span-5 md:col-span-3">
          <h2 className="text-3xl font-extrabold leading-tight text-ink-950">{t(`${P}.siteHeadline`)}</h2>
          <p className="mt-3 max-w-md text-sm text-ink-600">{t(`${P}.siteBody`)}</p>
          <div className="mt-8 grid max-w-md grid-cols-3 gap-3">
            <div className="h-16 rounded-sm bg-ink-100" />
            <div className="h-16 rounded-sm bg-ink-100" />
            <div className="h-16 rounded-sm bg-ink-100" />
          </div>
        </div>
        <div className="hidden md:col-span-2 md:block">
          <div className="h-56 rounded-sm bg-ink-100" />
          <div className="mt-3 h-3 w-2/3 rounded-xs bg-ink-100" />
        </div>
      </div>
    </div>
  );
}

function Launcher({ form, open, onToggle }: { form: WidgetForm; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const withLabel = form.appearance.launcher_style === "label" && !open;
  const label = form.appearance.launcher_label || form.content.title;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={t(`${P}.toggle`)}
      className={cn("inline-flex h-12 items-center gap-2 rounded-pill text-sm font-bold shadow-pop", FILL, withLabel && label ? (form.appearance.launcher_icon_url ? "pl-2 pr-4" : "px-4") : "w-12 justify-center")}
    >
      {open ? <Icon name="x" className="size-5" /> : <LauncherMark iconUrl={form.appearance.launcher_icon_url} size={36} glyphClassName="size-5" />}
      {withLabel && label ? <span>{label}</span> : null}
    </button>
  );
}

function DesktopFrame({ form, agentName, defaultGreeting, siteName, domain }: Omit<WidgetPreviewProps, "device">) {
  const [open, setOpen] = useState(true);
  const left = form.appearance.position === "left";
  const theme = resolvedTheme(form.appearance.theme);
  const proactive = form.behavior.proactive.enabled && form.behavior.proactive.message && !open;
  return (
    <div className="overflow-hidden rounded-lg border border-line-strong shadow-pop">
      <div className="flex items-center gap-3 border-b border-ink-100 bg-ink-50 px-4 py-2.5" aria-hidden="true">
        <div className="flex gap-1.5">
          <span className="size-3 rounded-pill bg-ink-200" />
          <span className="size-3 rounded-pill bg-ink-200" />
          <span className="size-3 rounded-pill bg-ink-200" />
        </div>
        <div className="flex h-7 flex-1 items-center gap-2 rounded-sm border border-ink-100 bg-ink-50 px-3 font-mono text-[11px] text-ink-600">
          <Icon name="lock" className="size-3" />
          {domain ?? "example.com"}
        </div>
      </div>
      <div className="relative h-[640px] overflow-hidden">
        <MockSite siteName={siteName} domain={domain} />
        <div className={cn("absolute bottom-5 flex flex-col gap-3", left ? "left-5 items-start" : "right-5 items-end")}>
          {open ? (
            <div data-theme={theme} className="flex h-[520px] w-[360px] max-w-[calc(100vw-4rem)] flex-col overflow-hidden rounded-[var(--wg-radius)] border border-line bg-surface text-fg shadow-widget" data-testid="preview-panel">
              <WidgetPanelPreview form={form} agentName={agentName} defaultGreeting={defaultGreeting} variant="desktop" onClose={() => { setOpen(false); }} />
            </div>
          ) : null}
          {proactive ? (
            <div data-theme={theme} className="max-w-[260px] rounded-[var(--wg-radius)] border border-line bg-surface px-3.5 py-2.5 text-[12px] text-fg shadow-pop">
              {form.behavior.proactive.message}
            </div>
          ) : null}
          <Launcher form={form} open={open} onToggle={() => { setOpen(!open); }} />
        </div>
      </div>
    </div>
  );
}

function MobileFrame({ form, agentName, defaultGreeting }: Pick<WidgetPreviewProps, "form" | "agentName" | "defaultGreeting">) {
  const theme = resolvedTheme(form.appearance.theme);
  return (
    <div className="flex justify-center py-2">
      <div className="w-[360px] max-w-full rounded-[44px] border-[10px] border-ink-950 bg-ink-950 shadow-pop">
        <div data-theme={theme} className="relative flex h-[700px] flex-col overflow-hidden rounded-[34px] bg-surface text-fg" data-testid="preview-panel">
          <div aria-hidden="true" className={cn("flex items-center justify-between px-5 pb-1 pt-2.5 text-[11px] font-bold", FILL)}>
            <span>9:41</span>
            <span className="h-5 w-24 rounded-pill bg-ink-950" />
            <span>5G</span>
          </div>
          <WidgetPanelPreview form={form} agentName={agentName} defaultGreeting={defaultGreeting} variant="mobile" />
        </div>
      </div>
    </div>
  );
}

/**
 * Live preview of the draft. The real widget panel (apps/widget, Preact) can't render a draft
 * config in-process yet, so this draws the same layout with packages/chat pieces themed via
 * `--wg-*` custom properties (see report).
 */
export function WidgetPreview(props: WidgetPreviewProps) {
  const { t } = useTranslation();
  const contrast = brandContrast(props.form.appearance.primary_color);
  const { font } = props.form.appearance;
  useWidgetFontStylesheet(font);
  return (
    <ChatTheme primary={props.form.appearance.primary_color} onPrimary={contrast.onPrimary} radius={`${String(props.form.appearance.radius_px)}px`} font={widgetFontStack(font) ?? undefined}>
      <section aria-label={t(`${P}.region`)} data-testid="widget-preview">
        {props.device === "desktop" ? <DesktopFrame {...props} /> : <MobileFrame form={props.form} agentName={props.agentName} defaultGreeting={props.defaultGreeting} />}
      </section>
    </ChatTheme>
  );
}
